class BdlCategoriesCarousel extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-bdl-cat-track]');
    this.prevBtn = this.querySelector('[data-bdl-cat-prev]');
    this.nextBtn = this.querySelector('[data-bdl-cat-next]');
    this.dotsEl = this.querySelector('[data-bdl-cat-dots]');
    this.index = 0;
    this.swipeStartX = 0;
    this.swipeTracking = false;

    if (!this.scroller) return;

    this.prevBtn?.addEventListener('click', () => this.goBy(-1));
    this.nextBtn?.addEventListener('click', () => this.goBy(1));
    this.scroller.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.bindOneStepSwipe(this.scroller);
    this.resizeObserver = new ResizeObserver(() => {
      this.renderDots();
      this.snapToIndex(this.index, 'auto');
      this.updateButtons();
    });
    this.resizeObserver.observe(this.scroller);

    this.renderDots();
    this.snapToIndex(0, 'auto');
    this.updateButtons();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.scrollTimer);
    this.unbindOneStepSwipe();
  }

  /**
   * Mobile: um passo por gesto (igual às setas). touch-action: pan-y no CSS
   * bloqueia o scroll livre; o swipe só chama goBy(±1).
   */
  bindOneStepSwipe(el) {
    this._onSwipeDown = (event) => {
      if (event.pointerType === 'mouse') return;
      this.swipeTracking = true;
      this.swipeStartX = event.clientX;
    };
    this._onSwipeUp = (event) => {
      if (!this.swipeTracking) return;
      this.swipeTracking = false;
      if (event.pointerType === 'mouse') return;

      const dx = event.clientX - this.swipeStartX;
      const threshold = Math.min(56, Math.max(36, el.clientWidth * 0.12));
      if (dx <= -threshold) this.goBy(1);
      else if (dx >= threshold) this.goBy(-1);
      else this.snapToIndex(this.index);
    };
    this._onSwipeCancel = () => {
      this.swipeTracking = false;
    };

    el.addEventListener('pointerdown', this._onSwipeDown);
    el.addEventListener('pointerup', this._onSwipeUp);
    el.addEventListener('pointercancel', this._onSwipeCancel);
  }

  unbindOneStepSwipe() {
    if (!this.scroller) return;
    this.scroller.removeEventListener('pointerdown', this._onSwipeDown);
    this.scroller.removeEventListener('pointerup', this._onSwipeUp);
    this.scroller.removeEventListener('pointercancel', this._onSwipeCancel);
  }

  cards() {
    return Array.from(this.querySelectorAll('[data-bdl-cat-card]'));
  }

  gap() {
    const track = this.scroller?.querySelector('.bdl-categories__track');
    if (!track) return 0;
    const styles = getComputedStyle(track);
    return Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
  }

  visibleCount() {
    const cards = this.cards();
    if (!cards.length) return 1;
    const cardWidth = cards[0].getBoundingClientRect().width;
    const step = cardWidth + this.gap();
    if (step <= 0) return 1;
    return Math.max(1, Math.floor((this.scroller.clientWidth + this.gap() + 1) / step));
  }

  maxIndex() {
    const total = this.cards().length;
    const visible = this.visibleCount();
    return Math.max(0, total - visible);
  }

  pageIndexes() {
    const cards = this.cards();
    const max = this.maxIndex();
    if (!cards.length || max === 0) return [0];

    const step = cards[0].getBoundingClientRect().width + this.gap();
    if (step <= 0) return [0];
    const distance = this.scroller.scrollWidth - this.scroller.clientWidth;
    const pageCount = Math.max(1, Math.ceil(max / this.visibleCount()) + 1);
    const pages = [0];

    for (let page = 1; page < pageCount; page += 1) {
      pages.push(Math.round((distance * page) / pageCount / step));
    }
    pages.push(max);
    return [...new Set(pages.map((n) => Math.min(Math.max(0, n), max)))];
  }

  cardScrollLeft(card) {
    const track = this.scroller.querySelector('.bdl-categories__track');
    if (!track) return card.offsetLeft;
    return card.offsetLeft - track.offsetLeft;
  }

  goBy(direction) {
    const pages = this.pageIndexes();
    const nextIndex = direction > 0
      ? pages.find((index) => index > this.index) ?? this.maxIndex()
      : [...pages].reverse().find((index) => index < this.index) ?? 0;

    this.snapToIndex(nextIndex);
  }

  snapToIndex(nextIndex, behavior = 'smooth') {
    const cards = this.cards();
    if (!cards.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'instant';

    this.index = Math.min(Math.max(0, nextIndex), this.maxIndex());
    const target = cards[this.index];
    const left = this.index === this.maxIndex()
      ? this.scroller.scrollWidth - this.scroller.clientWidth
      : this.cardScrollLeft(target);

    window.clearTimeout(this.scrollTimer);
    this.scroller.scrollTo({ left, behavior });
    this.updateButtons();
  }

  onScroll() {
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      const cards = this.cards();
      if (!cards.length) return;

      const scrollLeft = this.scroller.scrollLeft;
      let closest = 0;
      let closestDist = Infinity;

      cards.forEach((card, i) => {
        const dist = Math.abs(this.cardScrollLeft(card) - scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });

      const atEnd = this.scroller.scrollWidth - this.scroller.clientWidth - scrollLeft <= 1;
      this.index = atEnd ? this.maxIndex() : Math.min(closest, this.maxIndex());
      this.updateButtons();
    }, 90);
  }

  renderDots() {
    if (!this.dotsEl) return;
    const pages = this.pageIndexes();
    this.dotsEl.innerHTML = '';

    if (pages.length <= 1) {
      this.dotsEl.hidden = true;
      return;
    }

    this.dotsEl.hidden = false;
    pages.forEach((pageIndex, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bdl-categories__dot';
      btn.setAttribute('aria-label', `Ir para página ${i + 1}`);
      btn.addEventListener('click', () => this.snapToIndex(pageIndex));
      this.dotsEl.appendChild(btn);
    });
    this.syncDots();
  }

  syncDots() {
    if (!this.dotsEl) return;
    const pages = this.pageIndexes();
    const buttons = Array.from(this.dotsEl.querySelectorAll('.bdl-categories__dot'));
    let active = 0;
    pages.forEach((pageIndex, i) => {
      if (pageIndex <= this.index) active = i;
    });
    buttons.forEach((btn, i) => {
      btn.setAttribute('aria-current', i === active ? 'true' : 'false');
    });
  }

  updateButtons() {
    const max = this.maxIndex();
    if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= max;
    this.syncDots();
  }
}

if (!customElements.get('bdl-categories-carousel')) {
  customElements.define('bdl-categories-carousel', BdlCategoriesCarousel);
}
